<template>
  <div class="banner">
    <div
      v-if="!isMobile"
      class="banner-pc"
    >
      <div class="download-app">
        <div class="download-banner">
          <picture class="hero">
            <source
              srcset="@/assets/images/pc/banner.webp"
              type="image/webp"
            />
            <img
              src="@/assets/images/pc/banner.jpg"
              width="3840"
              height="1560"
              fetchpriority="high"
              alt=""
            />
          </picture>
          <ul class="download-list">
            <li
              v-for="item in downloadList"
              :key="item.name"
              class="download-item"
              role="button"
              tabindex="0"
              @click="goDownloadItem(item)"
            >
              <img
                :src="item.img"
                alt=""
              />
              <span>{{ item.name }}</span>
            </li>
          </ul>
        </div>
        <img
          src="@/assets/images/pc/banner1.jpg"
          class="banner1"
          width="2400"
          height="6376"
          loading="lazy"
          decoding="async"
          alt=""
        />
        <img
          src="@/assets/images/pc/banner2.png"
          class="banner2"
          width="2400"
          height="1668"
          loading="lazy"
          decoding="async"
          alt=""
        />
      </div>
    </div>
    <!-- h5 -->
    <BannerH5
      v-else
      class="banner-h5-wrap"
      @download="goDownload"
    />
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { DownloadUrl } from '@local-utils/const';
import { useRoute } from 'vue-router';
import { logEvent } from '@/utils/analytics';
import { useAttribution } from '@local-composable/common';
import { useRequest } from '@local-composable/request';
import { showLoadingToast } from 'vant';
import android from '@/assets/images/pc/android.png';
import apple from '@/assets/images/pc/apple.png';
import google from '@/assets/images/pc/google.png';
import BannerH5 from './banner-h5.vue';

defineProps({ isMobile: Boolean });

const route = useRoute();
const { toDownload } = useAttribution({ channel: route.query.channel, isToStore: false });

const downloadList = [
  {
    name: 'Google Play Store',
    img: google,
    tag: 'google',
  },
  {
    name: 'App Store',
    img: apple,
    tag: 'ios',
  },
  {
    name: 'Android',
    img: android,
    tag: 'android',
  },
];

const link = ref('');
onMounted(async () => {
  const { data } = await useRequest({
    url: '/v1/download/link',
    data: {
      sys: 0,
    },
  }).get();

  if (data.value) {
    link.value = data.value.link;
  }
});

const goDownload = async () => {
  const loading = showLoadingToast({
    duration: 0,
    forbidClick: true,
    message: 'loading...',
  });
  try {
    await toDownload();
  } catch (e) {
    console.error(e);
  } finally {
    loading.close();
    window.location.href = link.value;
  }
};

function goDownloadItem(item) {
  if (item.tag == 'ios') {
    logEvent('official_apple_click');
    window.open(DownloadUrl.ios);
  } else if (item.tag == 'google') {
    logEvent('official_android_click');
    window.open(DownloadUrl.android);
  } else {
    goDownload();
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/common.scss';

.banner {
  max-width: $pc-max-width;
  background-color: #ffffff;
  overflow: hidden;
  padding-bottom: 2rem;

  .download-app {
    .download-banner {
      position: relative;
      display: flex;
      flex-direction: column;
      justify-content: flex-end;
      align-items: center;
      box-sizing: border-box;
      width: 100%;
      aspect-ratio: 3840 / 1560;
      padding: 0 0.4rem 2.5rem;

      .hero {
        position: absolute;
        inset: 0;
        pointer-events: none;

        img {
          display: block;
          width: 100%;
          height: auto;
        }
      }

      .download-list {
        position: relative;
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
        align-items: stretch;
        gap: 0.16rem;
        margin: 0;
        padding: 0;
        list-style: none;
      }

      .download-item {
        width: 4rem;
        height: 0.8rem;
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        gap: 0.12rem;
        min-height: 0.56rem;
        padding: 0 0.22rem;
        border-radius: 0.12rem;
        background: linear-gradient(to bottom, #ffe7a8, #fff5d7);
        border: 1px solid #ffe7aa;
        cursor: pointer;

        img {
          flex-shrink: 0;
          width: 0.36rem;
          height: 0.36rem;
          object-fit: contain;
        }

        span {
          font-size: 0.28rem;
          line-height: 1.2;
          font-family: Georgia, 'Times New Roman', Times, serif;
          font-weight: 500;
          color: #1a1208;
          white-space: nowrap;
        }
      }
    }

    .banner1 {
      position: relative;
      z-index: 5;
      display: block;
      width: 62.5%;
      height: auto;
      aspect-ratio: 2400 / 6376;
      margin: -1rem auto 0;
    }

    .banner2 {
      display: block;
      width: 62.5%;
      height: auto;
      aspect-ratio: 2400 / 1668;
      margin: 1.5rem auto 0;
    }
  }

  .more {
    display: none;
  }

  .banner-h5-wrap {
    display: none;
  }
}

@media (max-width: 768px) {
  .banner {
    max-width: none;
    padding-bottom: 0;

    .banner-pc {
      display: none;
    }

    .banner-h5-wrap {
      display: block;
    }
  }
}
</style>
